import { IN_APP_EVENT_NAMES, RESERVED_EVENT_NAMES } from '../contract/event-contract';
import type { Clock } from '../core/clock';
import type { SessionManager } from '../core/session';
import { randomUUID } from '../core/uuid';
import type { Logger } from '../logging/logger';
import type { EventsHandler } from './events-handler';
import type { QueuedEvent } from './event';
import { sanitizeProperties } from './sanitize';
import { truncate } from './enrich';

export const SCREEN_VIEW_EVENT = 'screen_view';

/** Matches the same-name dedup window in CustomEventsHandler on iOS. */
const SCREEN_DEDUP_MS = 1000;

// Shared across clients: a reconfigure's fresh handler re-reported the same screen (T9).
let lastScreenName: string | null = null;
let lastScreenAt = 0;

/** Test seam: the dedup outlives individual handlers. */
export function __resetScreenDedup(): void {
  lastScreenName = null;
  lastScreenAt = 0;
}

/** What was queued, as the in-app engine needs it. Null when the call was refused or deduplicated. */
export interface Tracked {
  name: string;
  properties: Record<string, unknown>;
}

export interface CustomEventsDeps {
  events: EventsHandler;
  session: SessionManager;
  clock: Clock;
  logger: Logger;
  currentPath: () => string | null;
}

/**
 * Custom events and screen views.
 *
 * Every event routes through EventsHandler.enqueue, which routes through
 * enrich() — no body is constructed here (spec A4/T12).
 */
export class CustomEventsHandler {
  /** Stamped onto custom events so they can be segmented by screen. */
  private currentScreenName: string | null = null;

  constructor(private readonly deps: CustomEventsDeps) {}

  track(name: string, properties?: Record<string, unknown>, tags?: string[]): Tracked | null {
    const trimmed = name?.trim() ?? '';

    if (!trimmed) {
      this.deps.logger.warn('track() requires a non-empty event name; the call was ignored.');
      return null;
    }

    // The backend rejects these with a 400. Failing here, with the reason,
    // beats a rejection the integrator only sees in a dashboard that stays
    // empty.
    if (RESERVED_EVENT_NAMES.has(trimmed) || IN_APP_EVENT_NAMES.has(trimmed)) {
      this.deps.logger.warn(
        `"${trimmed}" is a reserved system event name and cannot be used with track(). ` +
          'Choose a different name.',
      );
      return null;
    }

    if (trimmed === SCREEN_VIEW_EVENT) {
      this.deps.logger.warn(
        'Use trackScreenView() rather than track("screen_view") so the screen name is attached.',
      );
      return null;
    }

    // Bounded here, not only at send: one unbounded name could exceed the
    // queue's whole budget and cost the events queued before it.
    const queued = truncate(trimmed);
    return { name: queued, properties: this.enqueue(queued, properties, tags) };
  }

  /**
   * Screen views carry screen_name in properties, and set the screen context
   * stamped onto later custom events.
   *
   * The 1-second same-name dedup stops tab switches and orientation changes
   * flooding the dashboard; it resets on session rotation, because the same
   * screen in a new session is a genuinely new view.
   */
  trackScreenView(screenName: string, properties?: Record<string, unknown>): Tracked | null {
    // Bounded before it becomes context: an oversized name would otherwise
    // survive the 8 KB cap through the context fallback below and ride on
    // every later custom event, all of which the backend then rejects.
    const trimmed = truncate(screenName?.trim() ?? '');
    if (!trimmed) {
      this.deps.logger.warn('trackScreenView() requires a non-empty screen name.');
      return null;
    }

    if (this.deps.session.rotateIfIdle()) this.resetDedup();

    // Set before the dedup returns, or a deduped view leaves later custom
    // events with no screen context.
    this.currentScreenName = trimmed;

    const now = this.deps.clock.now();
    if (lastScreenName === trimmed && now - lastScreenAt < SCREEN_DEDUP_MS) return null;

    lastScreenName = trimmed;
    lastScreenAt = now;

    // Passed separately, not spread in: a spread reads every value, and a
    // throwing getter must cost its key rather than the caller's call.
    return { name: trimmed, properties: this.enqueue(SCREEN_VIEW_EVENT, properties, undefined, { screen_name: trimmed }) };
  }

  resetDedup(): void {
    __resetScreenDedup();
  }

  resetScreenContext(): void {
    this.currentScreenName = null;
  }

  private enqueue(
    eventName: string,
    properties?: Record<string, unknown>,
    tags?: string[],
    extra?: Record<string, string>,
  ): Record<string, unknown> {
    // Sanitized before the merge, never after: merging means spreading, and a
    // spread reads every value.
    const base = sanitizeProperties(properties, this.deps.logger);
    const context = extra ?? this.screenContext(eventName);
    // The second pass re-applies the 8 KB cap to the merged object. Properties
    // that only exceed it once the context is added cost the properties, not
    // the context — a screen view must keep its screen_name either way.
    const sanitized = context
      ? (sanitizeProperties({ ...base, ...context }, this.deps.logger) ?? context)
      : base;

    const event: QueuedEvent = {
      id: randomUUID(),
      eventName,
      createdAt: this.deps.clock.now(),
      sessionId: this.deps.session.currentSessionId(),
    };

    const path = this.deps.currentPath();
    if (path) event.path = path;
    if (sanitized) event.properties = sanitized;

    const merged = this.deps.events.mergeTags(tags);
    if (merged) event.tags = merged;

    this.deps.events.enqueue(event);
    return sanitized ?? {};
  }

  /** Custom events carry the most recently viewed screen, matching iOS. */
  private screenContext(eventName: string): Record<string, string> | undefined {
    if (eventName === SCREEN_VIEW_EVENT) return undefined;
    if (!this.currentScreenName) return undefined;
    return { screen_name: this.currentScreenName };
  }
}
